// Чистая логика «показывать ли in-app уведомление» (SPEC §4.5). Вынесена отдельно — её ловит
// инвариант-тест (активный чат → нет тоста; muted → нет; mentions-режим → только @me/reply-на-своё).
// Никакого React/сети — только решение.

import type { Message, NotifySettings, UserId } from '@tele-chat/shared-contracts'

export interface NotifyContext {
  /** Текущий пользователь — своё сообщение не уведомляет. */
  selfId: UserId
  /** Активно открытый чат (фокус вкладки + он на экране) — по нему НЕ дребезжим (SPEC §4.7 гонка). */
  activeChatId: string | null
  /** Вкладка в фокусе. Если нет — in-app тост всё равно уместен (но pwa-push отдельно). */
  windowFocused: boolean
  /** Чат в mute. */
  muted: boolean
  /** Настройки уведомлений (mode/preview/sound). */
  settings: NotifySettings
  /** Текущее время, мс (инъекция для тестов; по умолчанию Date.now). */
  nowMs?: number
}

/**
 * BR-10: история, импортированная мостом «задним числом» (origin + createdAt давно в прошлом), — не
 * повод для тоста: импорт идёт без уведомлений (FR-BRIDGE-04), а живой кадр message.new флаг silent
 * не несёт. Свежие сообщения из Telegram уведомляют как обычные (FR-BRIDGE-05).
 */
export const IMPORTED_HISTORY_AGE_MS = 5 * 60_000

export interface NotifyDecision {
  /** Показать ли тост. */
  toast: boolean
  /** Проиграть ли звук (только если toast и settings.sound). */
  sound: boolean
  /** Текст превью (пусто при preview=false — показываем «Новое сообщение»). */
  showPreview: boolean
}

const SUPPRESS: NotifyDecision = { toast: false, sound: false, showPreview: false }

/** Содержит ли сообщение упоминание текущего пользователя или это reply на его сообщение. */
export function isMentionOfSelf(message: Message, selfId: UserId, selfMessageReplied: boolean): boolean {
  if (selfMessageReplied) return true
  if (message.mentions && message.mentions.includes(selfId)) return true
  return false
}

/**
 * Решение по новому ВХОДЯЩЕМУ сообщению. Вход — message + контекст + признак «reply на своё»
 * (его считает вызывающий по replyTo.senderId). Возвращает: тост/звук/превью.
 */
export function decideInAppNotification(
  message: Message,
  ctx: NotifyContext,
  selfMessageReplied: boolean,
): NotifyDecision {
  // Своё сообщение — никогда (нет «эха» по себе).
  if (message.senderId === ctx.selfId) return SUPPRESS
  // Тумбстоун/системное — не уведомляем.
  if (message.deletedAt !== undefined || message.kind === 'system') return SUPPRESS
  // Импорт истории мостом — не уведомляем (BR-10).
  if (
    message.origin !== undefined &&
    (ctx.nowMs ?? Date.now()) - Date.parse(message.createdAt) > IMPORTED_HISTORY_AGE_MS
  ) {
    return SUPPRESS
  }
  // Активно открытый чат при фокусе вкладки — нет дребезга (SPEC §4.7).
  if (ctx.windowFocused && ctx.activeChatId === message.chatId) return SUPPRESS
  // Mute — нет тоста/звука (FR-NOTIF, mute глушит in-app тоже).
  if (ctx.muted) return SUPPRESS
  // Режим «только упоминания» (FR-NOTIF-05): тост лишь при @me / reply-на-своё.
  if (ctx.settings.mode === 'mentions' && !isMentionOfSelf(message, ctx.selfId, selfMessageReplied)) {
    return SUPPRESS
  }
  return {
    toast: true,
    sound: ctx.settings.sound,
    showPreview: ctx.settings.preview,
  }
}
